import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Button, Icons, Tooltip } from '@/js/libs/ui/src/V2';

import { ROUTER } from 'src/AppRouter';
import { useNavigationContext } from '../../context';
import { MailIconIndicator, MailIconWrapper } from './styled';

export const NotificationsButton = memo(function NotificationsButton({ isCollapsedNav }: { isCollapsedNav: boolean }) {
  const [t] = useTranslation(['backend', 'common']);
  const { colors } = useTheme();

  const { unreadCommentsCountRequest, unreadSystemNotificationsCountRequest } = useNavigationContext();

  const unreadCommentsCount = useSubscribe(unreadCommentsCountRequest.data$, (data) => data?.data ?? 0);
  const unreadCommentsCountError = useSubscribe(unreadCommentsCountRequest.error$);

  const unreadSystemNotificationsCount = useSubscribe(unreadSystemNotificationsCountRequest.data$, (data) => data?.data ?? 0);
  const unreadSystemNotificationsCountError = useSubscribe(unreadSystemNotificationsCountRequest.error$);

  const errorMessage = unreadSystemNotificationsCountError
    ? t('common:SYSTEM_NOTIFICATIONS_UNAVAILABLE')
    : unreadCommentsCountError
      ? t('common:MESSAGES_SERVICE_UNAVAILABLE')
      : undefined;

  const onIconClick = useCallbackRef(() => {
    if (unreadCommentsCount > 0 && unreadSystemNotificationsCount === 0) {
      ROUTER['/notifications/comments'].path.navigate();
    } else {
      ROUTER['/notifications/system'].path.navigate();
    }
  });

  return (
    <Button iconOnly ghost onClick={onIconClick} dataTestId='mail-button'>
      {errorMessage ? (
        <Tooltip ContainerComponent={MailIconWrapper} tooltip={errorMessage}>
          <MailIconIndicator critical $isCollapsedNav={isCollapsedNav} />
          <Icons.Mail color={colors.grey100_1} />
        </Tooltip>
      ) : unreadSystemNotificationsCount > 0 ? (
        <Tooltip
          ContainerComponent={MailIconWrapper}
          tooltip={t('common:UNREAD_SYSTEM_NOTIFICATIONS_COUNT_TOOLTIP', { count: unreadSystemNotificationsCount })}>
          <MailIconIndicator warning $isCollapsedNav={isCollapsedNav} />
          <Icons.Mail color={colors.grey100_1} />
        </Tooltip>
      ) : unreadCommentsCount > 0 ? (
        <Tooltip ContainerComponent={MailIconWrapper} tooltip={t('common:UNREAD_MESSAGES_COUNT_TOOLTIP', { count: unreadCommentsCount })}>
          <MailIconIndicator positive $isCollapsedNav={isCollapsedNav} />
          <Icons.Mail color={colors.grey100_1} />
        </Tooltip>
      ) : (
        <Icons.Mail color={colors.grey100_1} />
      )}
    </Button>
  );
});
